import { UserId } from '@business/entities/account';
import { ContactId } from '@business/entities/contact';
import { LabourTypeId } from '@business/entities/labour-type';
import { OrderProductId } from '@business/entities/order';
import { OrderPackageId } from '@business/entities/order/OrderPackageResponse';
import { ProposalDepositModule, ProposalDiscountType, ProposalTemplateId } from '@business/entities/proposal';
import { TMEstimateId } from '@business/entities/tm';
import { EstimatorTemplateListWithSelectionResponse } from '@business/entities/tm/EstimatorTemplateListResponse';
import { EstimateService } from '@business/services';
import { formArray, formControl, FormDataType, formGroup, Validators } from '@core/utils/form';
import { Session } from '@modules/auth/session';
import { defineEstimateForm, defineEstimateItemGroupForm } from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';
import { defineProposalForm } from '@modules/proposal/ProposalBuilderPage';
import { defineLabourForm } from '@modules/timeMaterials/AddEditTimeMaterialPage/TimeMaterialForm';

function defineSigneeForm() {
  return formGroup({
    id: formControl<ContactId>({ required: true }),
    name: formControl<string>({ required: true }),
    email: formControl<string>({ required: true }),
  });
}
export type SigneeForm = ReturnType<typeof defineSigneeForm>;
export type SigneeFormData = FormDataType<typeof defineSigneeForm>;

export function defineAddProposalForm(requireAddress?: boolean) {
  return formGroup({
    templateId: formControl<ProposalTemplateId>({ required: true }),
    // Customer Design File chosen in the Design File step — optional (see Steps/DesignFileStep).
    designFileId: formControl<string>({}),
    // Name/URL of that file, kept alongside the id so the Review preview resolves design-file links
    // before the proposal is saved (the API only returns `designFile` for a saved proposal).
    designFileUrl: formControl<string>({}),
    designFileName: formControl<string>({}),
    orgId: formControl<string>({}),
    orgName: formControl<string>({}),
    addressId: formControl<string>({ required: requireAddress || false, label: 'Address' }),
    title: formControl<string>({ label: 'Proposal Title', required: true }),
    startDate: formControl<number>({ label: 'Start Date', required: true }),
    weeksToComplete: formControl<number>({ label: 'Weeks to Complete', required: true, initialValue: 1 }),
    proposal: defineProposalForm(),
    estimate: defineEstimateForm(),
    // packages: formArray(() => definePackageForm()),
    // subProjects: formArray(() => defineSubProjectsForm()),
    subProjectIds: formControl<TMEstimateId[]>({}),
    listItemIds: formControl<string[]>({ initialValue: [] }),
    signee: formArray(() => defineSigneeForm()),
    total: formControl<number>({ required: true, initialValue: 0 }),
    grandTotal: formControl<number>({ required: true, initialValue: 0 }),
    email: formGroup({
      to: formControl<string[]>({
        placeholder: 'Enter Email(s)',
        label: 'To',
        required: true,
        validators: [Validators.emails],
      }),
      cc: formControl<string[]>({
        placeholder: 'CC',
        label: 'CC',
        validators: [Validators.emails],
      }),
      bcc: formControl<string[]>({
        placeholder: 'BCC',
        label: 'BCC',
        validators: [Validators.emails],
      }),
      body: formControl<string>({
        label: 'Email Message',
        required: true,
        initialValue: `<br /><br /><div class="gc-signature"><hr />${Session.user!.signature}</div>`,
      }),
    }),
    send: formControl<boolean>({
      initialValue: true,
    }),
    isDeposit: formControl<boolean>({ label: 'Do you want to take deposit?', initialValue: false }),
    deposit: formControl<number>({ initialValue: 0, label: 'Deposit Amount', validators: [Validators.number] }),
    depositEntered: formControl<number>({
      required: true,
      initialValue: 0,
      validators: [Validators.number],
    }),
    depositSubscription: formControl<boolean>({ initialValue: true, label: 'Create a new subscription' }),
    depositValueType: formControl<ProposalDiscountType>({
      initialValue: ProposalDiscountType.percentage,
    }),
    depositProject: formControl<boolean>({ initialValue: true, label: 'Collect one time deposit' }),
    createDefaultModule: formControl<ProposalDepositModule>({
      label: 'What should be created after collecting a deposit?',
      initialValue: ProposalDepositModule.workOrder,
    }),
    boardId: formControl<string>({ label: 'Board', initialValue: '' }),
    salesReps: formControl<UserId[]>({ label: 'Sales Rep', initialValue: [Session.user!.id] }),
  });
}

export type AddProposalForm = ReturnType<typeof defineAddProposalForm>;
export type AddProposalFormData = FormDataType<typeof defineAddProposalForm>;

export const defineLineItemForm = () => {
  return formGroup({
    productId: formControl<OrderProductId>({ required: true, placeholder: 'Search Product' }),
    serviceId: formControl<string>({ required: false, placeholder: 'Search Product' }),
    title: formControl<string>({ required: true, placeholder: 'Title' }),
    description: formControl<string>({ required: true, placeholder: 'Description' }),
    quantity: formControl<number>({ placeholder: '0', initialValue: 1, required: true }),
    price: formControl<number>({ placeholder: '0.00', initialValue: 0 }),
    labour: formControl<number | undefined>({ required: false }),
    labourType: formControl<LabourTypeId | undefined>({ required: false }),
    labourPrice: formControl<number | undefined>({ required: false }),
    labourCost: formControl<number | undefined>({ required: false }),
    labourItems: formArray(() => defineLabourForm()),
    cost: formControl<number>({ placeholder: '0.00', initialValue: 0 }),
    isChecked: formControl<boolean>({ initialValue: false }),
  });
};

export type PackageProductLineItemFormData = FormDataType<typeof defineLineItemForm>;
export type PackageProductLineItemFormType = ReturnType<typeof defineLineItemForm>;

export function definePackageForm() {
  return formGroup({
    id: formControl<OrderPackageId>({
      placeholder: 'Package',
      required: true,
    }),
    title: formControl<string>({
      placeholder: 'Title',
      required: true,
    }),
    description: formControl<string>({
      placeholder: 'Description',
      required: true,
    }),
    image: formControl<File | string>({
      label: `Package Image`,
    }),
    lineItems: formArray(() => defineLineItemForm()),
  });
}

export type PackageFormData = FormDataType<typeof definePackageForm>;
export type PackageFormType = ReturnType<typeof definePackageForm>;

export function defineListItemsForm() {
  return formGroup({
    id: formControl<string>({}),
    listId: formControl<string>({}),
    title: formControl<string>({ required: true }),
    quantity: formControl<number>({ initialValue: 0 }),
    laborPrice: formControl<number>({ initialValue: 0 }),
    materialPrice: formControl<number>({ initialValue: 0 }),
    markupFactor: formControl<number>({ initialValue: 0 }),
    weProvide: formControl<boolean>({ initialValue: false }),
    total: formControl<number>({ initialValue: 0 }),
    isChecked: formControl<boolean>({ initialValue: false }),
  });
}

export type ListItemFormData = FormDataType<typeof defineListItemsForm>;
export type ListItemFormType = ReturnType<typeof defineListItemsForm>;

// Single source of truth for turning an estimate list item into proposal form data.
// quantity MUST include manualQty — the review/pricing block multiplies quantity by the
// unit prices, so dropping manualQty renders items whose qty was entered manually as $0.00.
// total uses the backend money math (markup included) so the proposal shows what the
// work order will bill — see EstimateService.getListItemAmounts.
export function listItemAmounts(item: EstimatorTemplateListWithSelectionResponse) {
  return EstimateService.getListItemAmounts({
    quantity: (item.estimateListItem?.quantity ?? 0) + (item.estimateListItem?.manualQty ?? 0),
    laborPrice: item.estimateListItem?.laborPrice ?? 0,
    materialPrice: item.estimateListItem?.materialPrice ?? 0,
    weProvide: item.estimateListItem?.weProvide ?? false,
    markupFactor: item.estimateListItem?.markupFactor ?? 0,
  });
}

export function mapListItemToForm(item: EstimatorTemplateListWithSelectionResponse) {
  const quantity = (item.estimateListItem?.quantity ?? 0) + (item.estimateListItem?.manualQty ?? 0);
  const laborPrice = item.estimateListItem?.laborPrice ?? 0;
  const materialPrice = item.estimateListItem?.materialPrice ?? 0;
  const markupFactor = item.estimateListItem?.markupFactor ?? 0;
  const weProvide = item.estimateListItem?.weProvide ?? false;

  return {
    id: item.estimateListItem?.id ?? '',
    listId: item.id,
    title: item.product?.title ?? '',
    quantity,
    laborPrice,
    materialPrice,
    markupFactor,
    weProvide,
    total: EstimateService.getListItemAmounts({ quantity, laborPrice, materialPrice, weProvide, markupFactor }).total,
  };
}

export function defineSubProjectsForm() {
  return formGroup({
    id: formControl<TMEstimateId>({
      placeholder: 'Estimate',
      required: true,
    }),
    title: formControl<string>({
      placeholder: 'Title',
      required: true,
    }),
    description: formControl<string>({
      placeholder: 'Description',
      required: true,
    }),
    fixedPrice: formControl<number>({
      placeholder: 'Fixed Price',
    }),
    vccAmount: formControl<number>({
      placeholder: 'VCC Amount',
    }),
    total: formControl<number>({
      placeholder: 'Amount',
      required: true,
    }),
    isChecked: formControl<boolean>({ initialValue: false }),
    formIds: formControl<string[]>({ initialValue: [] }),
    templateId: formControl<string>({}),
    groups: formArray(() => defineEstimateItemGroupForm()),
  });
}

export type SubProjectFormData = FormDataType<typeof defineSubProjectsForm>;
export type SubProjectFormType = ReturnType<typeof defineSubProjectsForm>;
